//src/pages/PatientInQueue/tokenIndex.jsx
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useState, useEffect } from "react";
import { 
  useTheme, 
  Typography, 
  Paper, 
  Chip, 
  CircularProgress, 
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Alert,
  styled,
  tableCellClasses,
  TextField,
  Divider,
  Stack,
  Grid,
  MenuItem,
} from "@mui/material";
import WaitingTokenTable from "../PatientInQueue/TokenDashboard";
import {
  getAllAppointments,
  generateTokenForAppointment,
  updateAppointment,
} from "../../services/appointmentService";
import { getDoctorById } from "../../services/doctorService";
import {
  getPatientById as fetchPatientById,
  syncPatientTypeForWorkflow,
} from "../../services/patientService";
import {
  getAppointmentDoctorId,
  getAppointmentDoctorName,
  getAppointmentPatientId,
  getAppointmentPatientName,
} from "../../utils/appointmentDisplayUtils";
import dayjs from "dayjs";
import { format } from "date-fns";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import PageShell from "../../components/PageShell";
import AddPatientModal from "../AddPatientModal/index.tsx";
import EditAppointmentModal from "../EditAppointmentModal/index.jsx";
import PaymentModeSelector from "../../components/PaymentModeSelector";
import {
  InvoiceAttachmentControl,
  InvoiceTotalsBox,
  InvoiceRemarksField,
  calcInvoiceGstBreakdown,
  resolveAttachmentUrl,
} from "../../components/InvoiceFormShared";
import { useOrganizationGstPercentage } from "../../hooks/useOrganizationGstPercentage";
import { useConfirmAppointmentBilling } from "../../hooks/useConfirmAppointmentBilling";
import { toast } from "react-toastify";
import { confirmAsync } from "../../utils/appNotify";
import { createInvoice } from "../../services/invoiceService";
import { uploadAttachment } from "../../services/uploadService";
import {
  scheduleRemindersForAppointment,
  cancelAppointmentReminders,
} from "../../services/appointmentReminderService";
import { useAuth } from "../../context/AuthContext";
import {
  getOrganizationId,
  getOrganizationName,
} from "../../utils/organizationUtils";
import { rememberInvoiceDepartment } from "../../utils/invoiceDepartmentMemory";
import {
  canStaffManualCancelRole,
  cancelAppointmentSoft,
  isAppointmentCancelled,
  isAppointmentCompleted,
  getAppointmentDisplayStatus,
  getAppointmentCancelReasonText,
  shouldShowAppointmentCancelReason,
  isAppointmentStaffActionDisabled,
  getAppointmentStaffActionDisabledReason,
  needsFrontdeskConfirmation,
  buildRejectAppointmentPayload,
  getPaymentMethodUpper,
  hasAppointmentToken,
  shouldAutoGenerateToken,
  shouldShowGenerateAppointmentTokenButton,
  canGenerateAppointmentToken,
  getAppointmentTokenDisplay,
  extractTokenNumber,
  CANCEL_REASON_MANUAL,
} from "../../utils/appointmentCancelUtils";
import {
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
  TAB_PANEL_SX,
} from "../../utils/pageLayoutStyles";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const appointmentTableColumns = [
  { id: "id", label: "Appointment Id" },
  { id: "patientName", label: "Patient Name" },
  { id: "doctorName", label: "Doctor Name" },
  { id: "appointmentDate", label: "Appointment Date" },
  { id: "appointmentTime", label: "Appointment Time" },
  { id: "tokenNumber", label: "Token Number" },
  { id: "status", label: "Status" },
  { id: "cancelReason", label: "Cancel Reason" },
];

const APPOINTMENT_REFRESH_MS = 30000;

const TokenDashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const canManualCancelAppointment = canStaffManualCancelRole(user?.roleName);

  const [selectedTab, setSelectedTab] = useState("tokens");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Appointment management
  const [todayPage, setTodayPage] = useState(0);
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addPatientModalOpen, setAddPatientModalOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTargetAppointment, setCancelTargetAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState(CANCEL_REASON_MANUAL);
  const [editAppointmentModalOpen, setEditAppointmentModalOpen] = useState(false);
  const [appointmentInfo, setAppointmentInfo] = useState({});
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [departmentName, setDepartmentName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [patientInfo, setPatientInfo] = useState({});
  const [selectedPatientForAppointment, setSelectedPatientForAppointment] = useState(null);

  // Patient-booked appointment confirm/reject (same flow as previous Appointments By Patient tab)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedConfirmAppointment, setSelectedConfirmAppointment] = useState(null);
  const [confirmProcessing, setConfirmProcessing] = useState(false);
  const [appointmentAmount, setAppointmentAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState({ mode: "cash" });
  const [invoiceAttachmentFile, setInvoiceAttachmentFile] = useState(null);
  const [invoiceAttachmentName, setInvoiceAttachmentName] = useState("");
  const [particulars, setParticulars] = useState("");
  const gstPercentage = useOrganizationGstPercentage(user, {
    enabled: confirmDialogOpen,
  });
  const {
    invoiceType,
    setInvoiceType,
    invoiceTypeOptions,
    amountError,
    submitDisabled,
  } = useConfirmAppointmentBilling({
    open: confirmDialogOpen,
    appointment: selectedConfirmAppointment,
    user,
    amount: appointmentAmount,
    paymentMode,
  });

  useEffect(() => {
    if (selectedTab !== "appointments") return undefined;

    fetchAppointmentData();
    const interval = setInterval(fetchAppointmentData, APPOINTMENT_REFRESH_MS);
    return () => clearInterval(interval);
  }, [selectedTab]);

  const fetchAppointmentData = async () => {
    try {
      setLoading(true);
      const appointmentsData = await getAllAppointments().catch((err) => {
        console.error("Error fetching appointments:", err);
        return [];
      });

      setAppointments(Array.isArray(appointmentsData) ? appointmentsData : []);
    } catch (error) {
      console.error("Error fetching appointment data:", error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Fetch doctor details for edit appointment
  const fetchDoctorDetails = async (doctorId) => {
    try {
      const doctor = await getDoctorById(doctorId);
      return doctor;
    } catch (error) {
      console.error("Error fetching doctor details:", error);
      return null;
    }
  };

  const fetchDepartmentName = async (departmentId, doctor) => {
    if (doctor?.departmentName) {
      setDepartmentName(doctor.departmentName);
      setDepartmentId(departmentId);
      return;
    }
    setDepartmentId(departmentId || "");
    setDepartmentName("");
  };

  // Handle edit appointment
  const handleEditAppointment = async (appointment) => {
    try {
      const patientId = getAppointmentPatientId(appointment);
      
      if (!patientId) {
        toast.error("Patient ID is missing in appointment. Cannot edit appointment.");
        return;
      }
      
      // Ensure patient is set before opening modal
      if (!selectedPatientForAppointment) {
        try {
          const fetchedPatient = await fetchPatientById(patientId);
          if (fetchedPatient) {
            setSelectedPatientForAppointment({
              id: fetchedPatient.id,
              name: fetchedPatient.name || fetchedPatient.firstName || getAppointmentPatientName(appointment),
              address: fetchedPatient.address || "",
              aadhar: fetchedPatient.aadhar || "",
              email: fetchedPatient.email || "",
              dob: fetchedPatient.dob || "",
              mobile: fetchedPatient.mobile || "",
              gender: fetchedPatient.gender || "",
            });
          } else {
            toast.error("Patient not found. Cannot edit appointment.");
            return;
          }
        } catch (error) {
          console.error("Error fetching patient:", error);
          toast.error("Failed to load patient details");
          return;
        }
      }
      
      // Ensure appointment has patientId for the modal
      const appointmentWithPatientId = { ...appointment, patientId };

      const doctorId = getAppointmentDoctorId(appointment);
      let doctor = null;
      
      if (doctorId && Number(doctorId) !== 0) {
        doctor = await fetchDoctorDetails(doctorId);
        if (doctor?.departmentId) {
          await fetchDepartmentName(doctor.departmentId, doctor);
        }
      }
      
      setAppointmentInfo(appointmentWithPatientId);
      setDoctorDetails(doctor);
      
      // Wait a moment to ensure state is set
      setTimeout(() => {
        setEditAppointmentModalOpen(true);
      }, 100);
    } catch (error) {
      console.error("Error fetching appointment data:", error);
      toast.error("Failed to load appointment details");
    }
  };

  // Handle create appointment
  const handleCreateAppointment = (patient = null) => {
    // If patient is provided (from appointment row), use it; otherwise create new patient appointment
    const patientData = patient || selectedPatientForAppointment;
    
    if (patientData) {
      setPatientInfo({
        id: patientData.id,
        name: patientData.name || patientData.patientName,
        address: patientData.address || "",
        aadhar: patientData.aadhar || "",
        email: patientData.email || "",
        dob: patientData.dob || "",
        mobile: patientData.mobile || "",
        gender: patientData.gender || "",
      });
    } else {
      // Create new patient appointment (empty patient_info will allow creating new patient)
      setPatientInfo({});
    }
    setAddPatientModalOpen(true);
  };

  // Handle cancel appointment (soft cancel — frontdesk / admin / super_admin)
  const handleCancelAppointment = (appointment) => {
    if (isAppointmentCancelled(appointment)) {
      toast.info("This appointment is already cancelled.");
      return;
    }
    if (isAppointmentCompleted(appointment)) {
      toast.info("Cannot cancel a completed appointment.");
      return;
    }
    setCancelTargetAppointment(appointment);
    setCancelReason(CANCEL_REASON_MANUAL);
    setCancelDialogOpen(true);
  };

  const handleCancelDialogConfirm = async () => {
    const appointment = cancelTargetAppointment;
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error("Cancellation reason is required.");
      return;
    }
    setCancelDialogOpen(false);
    try {
      await cancelAppointmentSoft(appointment, { reason });
      toast.success("Appointment cancelled successfully!");
      fetchAppointmentData();
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      if (error?.code === "ALREADY_CANCELLED" || error?.code === "ALREADY_COMPLETED") {
        toast.info(error.message);
        return;
      }
      toast.error("Failed to cancel appointment");
    } finally {
      setCancelTargetAppointment(null);
    }
  };

  // Handle generate token
  const handleGenerateToken = async (appointmentId, appointmentData = null) => {
    try {
      let appointment = appointmentData;
      if (!appointment) {
        appointment = appointments.find((apt) => apt.id === appointmentId);
      }

      const response = await generateTokenForAppointment(appointmentId, appointment);
      const tokenVal = extractTokenNumber(response);
      if (tokenVal != null) {
        toast.success(`Token generated successfully! Token #${tokenVal}`);
        // Refresh all appointments
        fetchAppointmentData();
      } else {
        toast.error("Failed to generate token.");
      }
    } catch (error) {
      console.error("Error generating token:", error);
      toast.error(error?.message || "Failed to generate token");
    }
  };

  const handleOpenConfirmAppointment = (appointment) => {
    setSelectedConfirmAppointment(appointment);
    setAppointmentAmount(0);
    setPaymentMode({ mode: "cash" });
    setInvoiceAttachmentFile(null);
    setInvoiceAttachmentName("");
    setParticulars("");
    setConfirmDialogOpen(true);
  };

  const handleConfirmAndGenerateToken = async () => {
    if (!selectedConfirmAppointment) return;

    if (submitDisabled) {
      toast.error(amountError || "Please enter the appointment amount.");
      return;
    }

    if (paymentMode.mode === "credit" && !paymentMode.organizationId) {
      toast.error("Please select an organization for credit payment.");
      return;
    }

    setConfirmProcessing(true);
    try {
      const subtotal = appointmentAmount || 0;
      const { sgst, cgst, totalAmount } = calcInvoiceGstBreakdown(
        subtotal,
        gstPercentage
      );

      let attachmentUrl = null;
      if (invoiceAttachmentFile instanceof File) {
        const uploaded = await uploadAttachment(invoiceAttachmentFile);
        attachmentUrl = resolveAttachmentUrl(uploaded);
      }

      const paymentModeValue = (paymentMode?.mode || "cash").toUpperCase();
      const creditOrganizationId =
        paymentModeValue === "CREDIT"
          ? paymentMode?.organizationId || null
          : null;
      const hospitalOrgId = getOrganizationId(user);
      const hospitalFromName = getOrganizationName(user) || "Aryan Hospital";
      const patientName =
        getAppointmentPatientName(selectedConfirmAppointment) || "";

      const appointmentId =
        selectedConfirmAppointment.id ||
        selectedConfirmAppointment.appointmentId ||
        null;
      const appointmentDoctorId =
        selectedConfirmAppointment.doctorId ||
        selectedConfirmAppointment.doctor_id ||
        null;
      const newInvoice = {
        patientId: selectedConfirmAppointment.patientId,
        doctorId:
          appointmentDoctorId != null && appointmentDoctorId !== ""
            ? Number(appointmentDoctorId)
            : undefined,
        appointmentId: appointmentId != null ? Number(appointmentId) : undefined,
        departmentId: (() => {
          const deptId =
            selectedConfirmAppointment.departmentId ||
            selectedConfirmAppointment.department_id ||
            departmentId;
          return deptId != null && deptId !== "" ? Number(deptId) : undefined;
        })(),
        amount: subtotal,
        sgst,
        cgst,
        totalAmount,
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        invoiceType,
        paymentMode: paymentModeValue,
        organizationId: hospitalOrgId || undefined,
        creditOrganizationId,
        billType: "OPD",
        fromName: hospitalFromName,
        toName: patientName || null,
        userId: user?.id || null,
        particulars: particulars?.trim?.() || "",
        ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
      };

      let invoice = null;
      try {
        invoice = await createInvoice(newInvoice);
      } catch (firstInvErr) {
        console.error("Invoice creation failed; retrying once:", firstInvErr);
        const { attachment, ...invoiceWithoutAttachment } = newInvoice;
        invoice = await createInvoice(invoiceWithoutAttachment);
      }
      rememberInvoiceDepartment(
        hospitalOrgId,
        {
          patientId: selectedConfirmAppointment.patientId,
          appointmentId:
            selectedConfirmAppointment.id || selectedConfirmAppointment.appointmentId,
          invoiceId: invoice?.id,
        },
        {
          departmentId:
            selectedConfirmAppointment.departmentId ||
            selectedConfirmAppointment.department_id,
          departmentName:
            selectedConfirmAppointment.departmentName ||
            selectedConfirmAppointment.department_name ||
            null,
          doctorId: selectedConfirmAppointment.doctorId,
        }
      );
      if (!invoice?.id) {
        toast.error("Failed to create invoice. Please try again.");
        setConfirmProcessing(false);
        return;
      }

      const updateData = {
        patientId: selectedConfirmAppointment.patientId,
        doctorId: selectedConfirmAppointment.doctorId,
        appointmentDateTime:
          selectedConfirmAppointment.appointmentDateTime ||
          selectedConfirmAppointment.appointmentDate ||
          selectedConfirmAppointment.date,
        status: "CONFIRMED",
        paymentMethod:
          getPaymentMethodUpper(selectedConfirmAppointment) || "PAY_AT_HOSPITAL",
        isCancel: selectedConfirmAppointment.isCancel || false,
      };

      const updatedAppointment = await updateAppointment(
        selectedConfirmAppointment.id,
        updateData
      );

      const generateTokenNow =
        shouldAutoGenerateToken(updateData.appointmentDateTime) &&
        !hasAppointmentToken(selectedConfirmAppointment);
      if (generateTokenNow) {
        await generateTokenForAppointment(selectedConfirmAppointment.id, {
          ...selectedConfirmAppointment,
          ...updateData,
          status: "CONFIRMED",
        });
      }

      try {
        await syncPatientTypeForWorkflow(
          selectedConfirmAppointment.patientId,
          "OUT",
          { force: true }
        );
      } catch (syncErr) {
        console.warn("syncPatientTypeForWorkflow failed after confirm:", syncErr);
      }

      try {
        const [patient, doctor] = await Promise.all([
          fetchPatientById(selectedConfirmAppointment.patientId).catch(() => null),
          getDoctorById(selectedConfirmAppointment.doctorId).catch(() => null),
        ]);

        await scheduleRemindersForAppointment(
          {
            ...updatedAppointment,
            appointmentDateTime:
              selectedConfirmAppointment.appointmentDateTime ||
              selectedConfirmAppointment.appointmentDate ||
              selectedConfirmAppointment.date,
            patientId: selectedConfirmAppointment.patientId,
            doctorId: selectedConfirmAppointment.doctorId,
            isVirtual:
              selectedConfirmAppointment.isVirtual ||
              selectedConfirmAppointment.is_virtual ||
              false,
            virtualLink:
              selectedConfirmAppointment.virtualLink ||
              selectedConfirmAppointment.virtual_link ||
              selectedConfirmAppointment.virtualMeeting?.url ||
              null,
          },
          {
            patient,
            doctor,
          }
        );
      } catch (reminderError) {
        console.warn("Failed to schedule appointment reminders:", reminderError);
      }

      toast.success(
        generateTokenNow
          ? "Appointment confirmed, invoice created, and token generated successfully!"
          : "Appointment confirmed and invoice created. Generate token on the appointment day."
      );
      setConfirmDialogOpen(false);
      setSelectedConfirmAppointment(null);
      setAppointmentAmount(0);
      setPaymentMode({ mode: "cash" });
      setInvoiceAttachmentFile(null);
      setInvoiceAttachmentName("");
      setParticulars("");
      setTimeout(() => {
        fetchAppointmentData();
      }, 500);
    } catch (error) {
      console.error("Error confirming appointment:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to confirm appointment. Please try again."
      );
    } finally {
      setConfirmProcessing(false);
    }
  };

  const handleRejectAppointment = async (appointment) => {
    const confirmed = await confirmAsync(
      "Are you sure you want to reject this appointment?",
      { title: "Reject appointment", tone: "warning", confirmLabel: "Reject" }
    );
    if (!confirmed) {
      return;
    }

    try {
      await updateAppointment(appointment.id, buildRejectAppointmentPayload(appointment));
      try {
        await cancelAppointmentReminders(appointment.id);
      } catch (reminderError) {
        console.warn("Failed to cancel appointment reminders:", reminderError);
      }
      toast.success("Appointment rejected.");
      fetchAppointmentData();
    } catch (error) {
      console.error("Error rejecting appointment:", error);
      toast.error("Failed to reject appointment. Please try again.");
    }
  };

  const handleChangeTodayPage = (event, newPage) => {
    setTodayPage(newPage);
  };

  const handleChangeUpcomingPage = (event, newPage) => {
    setUpcomingPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setTodayPage(0);
    setUpcomingPage(0);
  };

  // Staff see all appointments including cancelled; others see active only
  const allAppointments = appointments.filter((apt) => {
    if (canManualCancelAppointment) return true;
    return !isAppointmentCancelled(apt);
  });

  // Group appointments by date
  const today = dayjs().startOf("day");
  const nextWeek = dayjs().add(7, "days").endOf("day");

  const todayAppointments = allAppointments
    .filter((apt) => {
      const aptDate = dayjs(apt.appointmentDateTime || apt.appointmentDate || apt.date);
      return aptDate.isSame(today, "day");
    })
    .sort((a, b) => {
      const dateA = dayjs(a.appointmentDateTime || a.appointmentDate || a.date);
      const dateB = dayjs(b.appointmentDateTime || b.appointmentDate || b.date);
      return dateA.valueOf() - dateB.valueOf();
    });

  const upcomingWeekAppointments = allAppointments
    .filter((apt) => {
      const aptDate = dayjs(apt.appointmentDateTime || apt.appointmentDate || apt.date);
      return aptDate.isAfter(today, "day") && aptDate.isBefore(nextWeek);
    })
    .sort((a, b) => {
      const dateA = dayjs(a.appointmentDateTime || a.appointmentDate || a.date);
      const dateB = dayjs(b.appointmentDateTime || b.appointmentDate || b.date);
      return dateA.valueOf() - dateB.valueOf();
    });

  const getDoctorName = (appointmentOrDoctorId) => {
    if (appointmentOrDoctorId && typeof appointmentOrDoctorId === "object") {
      return getAppointmentDoctorName(appointmentOrDoctorId);
    }
    return getAppointmentDoctorName({ doctorId: appointmentOrDoctorId });
  };

  const getPatientName = (appointmentOrPatientId) => {
    if (appointmentOrPatientId && typeof appointmentOrPatientId === "object") {
      return getAppointmentPatientName(appointmentOrPatientId);
    }
    return getAppointmentPatientName({ patientId: appointmentOrPatientId });
  };

  const getStatusColor = (status) => {
    const statusUpper = (status || "").toUpperCase();
    if (statusUpper === "WAITING") return "#FFA500"; // yellow — token waiting
    if (statusUpper === "CALLED") return "#4CAF50"; // green — doctor called
    if (statusUpper === "CONFIRMED") return theme.palette.success.main;
    if (statusUpper === "PENDING_CONFIRMATION" || statusUpper === "PENDING") {
      return theme.palette.warning.main;
    }
    if (statusUpper === "COMPLETED") return theme.palette.info.main;
    if (statusUpper === "REJECTED" || statusUpper === "CANCELLED" || statusUpper === "CANCELED") {
      return theme.palette.error.main;
    }
    return theme.palette.grey[500];
  };

  const isRowActionDisabled = (row) => isAppointmentStaffActionDisabled(row);

  const renderAppointmentTable = (
    appointmentList,
    emptyMessage,
    page,
    onPageChange
  ) => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <CircularProgress />
        </Box>
      );
    }

    if (appointmentList.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    const paginatedAppointments = appointmentList.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {appointmentTableColumns.map((column) => (
                  <StyledTableCell key={column.id} align="left">
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell key="action" align="left">
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAppointments.map((row) => {
                const patientId = getAppointmentPatientId(row);
                const patientName = getAppointmentPatientName(row);
                const doctorName = getAppointmentDoctorName(row);
                const displayStatus = getAppointmentDisplayStatus(row);
                const statusColor = getStatusColor(displayStatus);
                const rowDisabled = isRowActionDisabled(row);
                const needsConfirm = needsFrontdeskConfirmation(row);
                const aptDate = dayjs(row.appointmentDateTime || row.appointmentDate || row.date);

                return (
                  <TableRow hover key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{patientName}</TableCell>
                    <TableCell>{doctorName}</TableCell>
                    <TableCell>{aptDate.format("D MMM YYYY")}</TableCell>
                    <TableCell>{aptDate.format("hh:mm A")}</TableCell>
                    <TableCell>
                      {shouldShowGenerateAppointmentTokenButton(row) ? (
                        <Button
                          onClick={() => handleGenerateToken(row.id, row)}
                          disabled={!canGenerateAppointmentToken(row)}
                          title={
                            canGenerateAppointmentToken(row)
                              ? "Generate token"
                              : "Token can be generated only on the appointment date"
                          }
                          sx={{
                            color: "green",
                            "&:hover": {
                              color: "darkgreen",
                            },
                          }}
                          size="small"
                        >
                          Generate Token
                        </Button>
                      ) : (
                        getAppointmentTokenDisplay(row) || "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={displayStatus}
                        size="small"
                        sx={{
                          backgroundColor: statusColor,
                          color: theme.palette.common.white,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {shouldShowAppointmentCancelReason(row) ? (
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.error.main, fontStyle: "italic" }}
                        >
                          {getAppointmentCancelReasonText(row)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {needsConfirm && (
                          <>
                            <IconButton
                              sx={{ color: theme.palette.success.main }}
                              onClick={() => handleOpenConfirmAppointment(row)}
                              size="small"
                              title="Confirm appointment"
                            >
                              <CheckCircleIcon />
                            </IconButton>
                            <IconButton
                              sx={{ color: theme.palette.error.main }}
                              onClick={() => handleRejectAppointment(row)}
                              size="small"
                              title="Reject appointment"
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          sx={{ 
                            color: patientId ? theme.palette.primary.main : theme.palette.grey[400],
                            cursor: patientId && !rowDisabled ? 'pointer' : 'not-allowed',
                          }}
                          disabled={!patientId || rowDisabled}
                          onClick={async () => {
                            if (rowDisabled) {
                              toast.info(
                                getAppointmentStaffActionDisabledReason(row) ||
                                  "Cannot edit this appointment",
                                { position: "top-center" }
                              );
                              return;
                            }

                            const actualPatientId = getAppointmentPatientId(row);
                            if (!actualPatientId) {
                              toast.error("Patient ID is missing in appointment. Cannot edit appointment.");
                              return;
                            }

                            try {
                              const fetchedPatient = await fetchPatientById(actualPatientId);
                              if (!fetchedPatient) {
                                toast.error("Patient not found. Cannot edit appointment.");
                                return;
                              }
                              setSelectedPatientForAppointment({
                                id: fetchedPatient.id,
                                name: fetchedPatient.name || fetchedPatient.firstName || getAppointmentPatientName(row),
                                address: fetchedPatient.address || "",
                                aadhar: fetchedPatient.aadhar || "",
                                email: fetchedPatient.email || "",
                                dob: fetchedPatient.dob || "",
                                mobile: fetchedPatient.mobile || "",
                                gender: fetchedPatient.gender || "",
                              });
                              handleEditAppointment({ ...row, patientId: actualPatientId });
                            } catch (error) {
                              console.error("Error fetching patient:", error);
                              toast.error("Failed to load patient details");
                            }
                          }}
                          size="small"
                          title={
                            rowDisabled
                              ? getAppointmentStaffActionDisabledReason(row) ||
                                "Cannot edit this appointment"
                              : !patientId
                                ? "Cannot edit: Patient ID missing in appointment"
                                : "Edit appointment"
                          }
                        >
                          <ModeEditOutlineTwoToneIcon />
                        </IconButton>
                        {canManualCancelAppointment && !isAppointmentCancelled(row) && (
                          <IconButton
                            sx={{
                              color: theme.palette.warning.main,
                              ...(rowDisabled && {
                                color: theme.palette.grey[400],
                                cursor: "not-allowed",
                              }),
                            }}
                            disabled={rowDisabled}
                            onClick={() => {
                              if (rowDisabled) {
                                toast.info(
                                  getAppointmentStaffActionDisabledReason(row) ||
                                    "Cannot cancel this appointment",
                                  { position: "top-center" }
                                );
                                return;
                              }
                              handleCancelAppointment(row);
                            }}
                            size="small"
                            title={
                              rowDisabled
                                ? getAppointmentStaffActionDisabledReason(row) ||
                                  "Cannot cancel this appointment"
                                : "Cancel appointment"
                            }
                          >
                            <CancelOutlined />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {appointmentList.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={appointmentList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </>
    );
  };

  return (
    <PageShell title="DASHBOARD">
      <TabContext value={selectedTab}>
        <Box sx={PAGE_TABS_BAR_SX}>
          <TabList
            {...PAGE_TAB_LIST_PROPS}
            onChange={handleChange}
          >
            <Tab label="Tokens" value="tokens" />
            <Tab label="Appointments" value="appointments" />
          </TabList>
        </Box>
        
        <TabPanel value="tokens" sx={TAB_PANEL_SX}>
          {selectedTab === "tokens" && <WaitingTokenTable />}
        </TabPanel>
        
        <TabPanel value="appointments" sx={TAB_PANEL_SX}>
          {selectedTab === "appointments" && (
          <>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1.5, 
                mb: 2.5,
                pb: 1.5,
                borderBottom: `2px solid ${theme.palette.primary.main}30`,
              }}>
                <CalendarTodayIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" fontWeight={600} sx={{ color: theme.palette.primary.main }}>
                  Today's Appointments
                </Typography>
                <Chip 
                  label={todayAppointments.length} 
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontWeight: 600,
                  }}
                />
              </Box>
              {renderAppointmentTable(
                todayAppointments,
                "No today's appointments",
                todayPage,
                handleChangeTodayPage
              )}
            </Box>

            <Box>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1.5, 
                mb: 2.5,
                pb: 1.5,
                borderBottom: `2px solid ${theme.palette.primary.main}30`,
              }}>
                <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" fontWeight={600} sx={{ color: theme.palette.primary.main }}>
                  Upcoming Week Appointments
                </Typography>
                <Chip 
                  label={upcomingWeekAppointments.length} 
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontWeight: 600,
                  }}
                />
              </Box>
              {renderAppointmentTable(
                upcomingWeekAppointments,
                "No upcoming week appointments",
                upcomingPage,
                handleChangeUpcomingPage
              )}
            </Box>

          {/* Modals */}
          <AddPatientModal
            open={addPatientModalOpen}
            onClose={() => {
              setAddPatientModalOpen(false);
              setPatientInfo({});
              setSelectedPatientForAppointment(null);
              fetchAppointmentData();
            }}
            patient_info={patientInfo}
          />
          <EditAppointmentModal
            open={editAppointmentModalOpen}
            onClose={() => {
              setEditAppointmentModalOpen(false);
              setAppointmentInfo({});
              setDoctorDetails(null);
              setSelectedPatientForAppointment(null);
            }}
            onSuccess={() => {
              // Refresh appointment data after successful update
              fetchAppointmentData();
            }}
            appointment={appointmentInfo}
            doctor={doctorDetails}
            departmentName={departmentName}
            departmentId={departmentId}
            patient={selectedPatientForAppointment}
          />

          {/* Confirm patient-booked appointment (same flow as previous Appointments By Patient) */}
          <Dialog
            open={confirmDialogOpen}
            onClose={() => !confirmProcessing && setConfirmDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={600}>Confirm Appointment</Typography>
                <IconButton
                  onClick={() => setConfirmDialogOpen(false)}
                  size="small"
                  disabled={confirmProcessing}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {selectedConfirmAppointment && (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Alert severity="info">
                    Please collect payment from the patient before confirming the appointment.
                  </Alert>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Patient: {getPatientName(selectedConfirmAppointment)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Doctor: {getDoctorName(selectedConfirmAppointment)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date:{" "}
                      {dayjs(
                        selectedConfirmAppointment.appointmentDateTime ||
                          selectedConfirmAppointment.appointmentDate
                      ).format("DD MMMM YYYY, hh:mm A")}
                    </Typography>
                  </Box>
                  <Divider />
                  <TextField
                    select
                    label="Invoice Type"
                    value={invoiceType}
                    onChange={(e) => setInvoiceType(e.target.value)}
                    fullWidth
                    disabled={confirmProcessing || invoiceTypeOptions.length <= 1}
                    helperText="Same as front-desk patient appointment billing"
                  >
                    {invoiceTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Appointment Amount"
                    type="number"
                    value={appointmentAmount}
                    onChange={(e) => setAppointmentAmount(parseFloat(e.target.value) || 0)}
                    fullWidth
                    required
                    error={Boolean(amountError)}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText={amountError || "Enter the consultation fee amount"}
                    disabled={confirmProcessing}
                  />
                  <InvoiceTotalsBox
                    subtotal={appointmentAmount || 0}
                    gstPercentage={gstPercentage}
                  />
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <PaymentModeSelector
                        value={paymentMode}
                        onChange={setPaymentMode}
                        disabled={confirmProcessing}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InvoiceAttachmentControl
                        attachmentName={invoiceAttachmentName}
                        attachmentFile={invoiceAttachmentFile}
                        onFileChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setInvoiceAttachmentFile(file);
                          setInvoiceAttachmentName(file.name);
                        }}
                        onDelete={() => {
                          setInvoiceAttachmentFile(null);
                          setInvoiceAttachmentName("");
                        }}
                        disabled={confirmProcessing}
                      />
                    </Grid>
                  </Grid>
                  <InvoiceRemarksField
                    name="particulars"
                    value={particulars}
                    onChange={(e) => setParticulars(e.target.value)}
                    disabled={confirmProcessing}
                  />
                  <Alert severity="success">
                    {shouldAutoGenerateToken(selectedConfirmAppointment)
                      ? 'Clicking "Confirm" will create an invoice, confirm the appointment, and generate a token number.'
                      : 'Clicking "Confirm" will create an invoice and confirm the appointment. Generate the token on the appointment day from the appointments table.'}
                  </Alert>
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setConfirmDialogOpen(false)}
                disabled={confirmProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAndGenerateToken}
                color="primary"
                variant="contained"
                disabled={confirmProcessing || submitDisabled}
              >
                {confirmProcessing
                  ? "Processing..."
                  : shouldAutoGenerateToken(selectedConfirmAppointment)
                    ? "Confirm & Generate Token"
                    : "Confirm Appointment"}
              </Button>
            </DialogActions>
          </Dialog>
          </>
          )}
        </TabPanel>
      </TabContext>

      {/* Cancel appointment dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          {cancelTargetAppointment && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {(() => {
                const aptDate = dayjs(
                  cancelTargetAppointment.appointmentDateTime ||
                    cancelTargetAppointment.appointmentDate ||
                    cancelTargetAppointment.date
                ).format("D MMM YYYY, hh:mm A");
                return `${getPatientName(cancelTargetAppointment)} · ${getDoctorName(cancelTargetAppointment)} · ${aptDate}`;
              })()}
            </Typography>
          )}
          <TextField
            label="Cancellation Reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            error={!cancelReason.trim()}
            helperText={!cancelReason.trim() ? "Reason is required" : ""}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <Button
            onClick={handleCancelDialogConfirm}
            color="error"
            variant="contained"
            disabled={!cancelReason.trim()}
          >
            Cancel Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default TokenDashboard;
